// Preset user lists for the SwitchUserMenu role-switcher.
//
// STAGING_USERS mirrors the ESS roster on the Indorama RFP staging
// account (`1138dbb7-f99a-4ce2-a250-fcc641b2076b`), refreshed
// 2026-07-27 from the live user list. Ordered by pipeline stage so
// testers can walk the full flow top-to-bottom:
//
//   submitted → hr → work_committee_* → steering_committee_*
//                  → implementation (head_of_department) → admin
//
// Labels carry the user's ESS role(s) + departments; where the user
// has a login email it's noted in a comment (handy for logging in
// directly instead of switching). Users are expected to be
// `user_info.ess_verified = true` — if a switch bounces to /profile,
// verify that user in staging first.
//
// The `role` field here is informational only (first/primary role for
// multi-role users) — the real gating in the app runs through
// `/auth/is_authorized` against `indorama.yaml`.
//
// Still unprovisioned in staging: superadmin (cross-app admin).
// approver + corporate_communication are covered by "Apple-testing".

export interface PresetUser {
  id: string;
  name: string;
  label: string;
  role?: string;
}

export const STAGING_USERS: PresetUser[] = [
  // Submitters
  {
    id: "c22bdb6e-fa3a-4b52-a11b-65992689ca94",
    label: "Operator — submitter (E2E)",
    name: "rishi-cont",
  },
  {
    id: "181548d0-227a-4236-b5ea-f38a9b83fd57",
    label: "Employee — pending ESS verification",
    name: "Asaimani chandrasekaran",
  },
  // HR
  {
    id: "86fb490e-7a8d-4d75-a1fa-af1c0a6500bb",
    label: "HR — PE, PP",
    name: "Rishi 1321",
    role: "hr",
  },
  {
    id: "9bb4d838-117b-46a9-ab9e-b963ef531dc1",
    label: "HR — PP, HSEF",
    name: "Rishi Op",
    role: "hr",
  },
  // Work Committee
  {
    // rishi@startraven.com
    id: "9e5408b2-6f93-40b8-a58d-8a02924223fa",
    label: "WC Chairman — PP, PE, Logistics, Adminstration, HSEF",
    name: "Rishi",
    role: "work_committee_chairman",
  },
  {
    // rishi+7@startraven.com
    id: "7287d716-919f-47e2-abb8-90272ab1fb88",
    label: "WC Chairman — PE",
    name: "Rishi Wcc",
    role: "work_committee_chairman",
  },
  {
    // rishi+2@startraven.com
    id: "8302547d-1778-42ac-81f4-62be643e2b6b",
    label: "WC Chairman — PE, PP",
    name: "Rishi Ess",
    role: "work_committee_chairman",
  },
  {
    // rishi+3@startraven.com
    id: "ed250e9b-a53a-4bbe-ab74-8a1cedc76cda",
    label: "WC Chairman + Facilitator + Member — PE, PP",
    name: "Rishi Ess Sc",
    role: "work_committee_chairman",
  },
  {
    id: "763620dc-41d9-48ec-adba-3a5e8992e9aa",
    label: "WC Facilitator — PE, PP",
    name: "Hr Rishi",
    role: "work_committee_facilitator",
  },
  {
    // rishi+10@startraven.com
    id: "bd667b6c-8aa6-45cc-8dba-5bbccda1a79d",
    label: "WC Chairman + Approver + Corp Comm + HR — Logistics, PE, PP",
    name: "Apple-testing",
    role: "work_committee_chairman",
  },
  // Steering Committee
  {
    id: "4c23bc32-b7f8-43c4-973f-6ed727805e7e",
    label: "SC Chairman + Facilitator + Member — HSEF, PP, PE, Logistics",
    name: "Rishii Sc",
    role: "steering_committee_chairman",
  },
  {
    id: "7487d1af-bdaf-4cb5-9eda-e2f697a3c9b9",
    label: "SC Chairman + WC Member — Adminstration, PP, PE",
    name: "Rishi 132",
    role: "steering_committee_chairman",
  },
  {
    // rishi+9@startraven.com
    id: "8b035e19-ecd2-47b4-928c-1618d65fa6d4",
    label: "SC Facilitator — HSEF",
    name: "Rishiiii",
    role: "steering_committee_facilitator",
  },
  {
    // rishi+4@startraven.com
    id: "a2f574aa-3146-4d9a-b0bf-3d428d3ede25",
    label: "SC Facilitator — Urea 1, Urea 2, Ammonia 1, Ammonia 2",
    name: "Rishi Hr",
    role: "steering_committee_facilitator",
  },
  // Head of Department
  {
    // rishi+31@startraven.com
    id: "ddf7315a-6d02-4592-9233-0e6dfce3d176",
    label: "Head of Department — HSEF, Adminstration, PE, PP, Logistics",
    name: "Rishi-20-may",
    role: "head_of_department",
  },
  // Admins
  {
    id: "74829f4e-0d58-4736-bd47-a91cd65692f0",
    label: "ESS Admin — PP, PE, HSEF, Logistics, Adminstration",
    name: "Rishi Sh Admin",
    role: "admin",
  },
  {
    // manikandan@indorama.com
    id: "cd0a54a8-2311-41c0-ae2b-60fddc4faca5",
    label: "ESS Admin — IFF (Instr, PHD, Mech, Logistics, Electrical), Ammonia 3",
    name: "Manikandan",
    role: "admin",
  },
];

export const TEST_PROD_USERS: PresetUser[] = [
  {
    id: "ec0922d2-72e9-40ca-9011-0d980c51ae16",
    label: "Operator - (Electrical - IPL)",
    name: "Test Operator",
  },
  {
    id: "aa635487-d709-45bc-98b2-687229a62aa7",
    label: "Safety Manager - (Electrical - IPL)",
    name: "Test Safety Manager",
  },
  {
    id: "627e3134-2125-4535-b440-6a4263c85210",
    label: "Head of Department - (Electrical - IPL)",
    name: "Test Head of Department",
  },
  {
    id: "118b7b6a-c9f6-49df-9b22-884e22666f7e",
    label: "HSEF Ambassador - (Electrical - IPL)",
    name: "Test HSEF Ambassador",
  },
  {
    id: "1dcae27a-5ab0-4e30-9ad3-53fbbb34d285",
    label: "Head of Section - (Plastics)",
    name: "Test Head of Section",
  },
  {
    id: "dad8cc68-ac92-4292-8eb5-7fab6010cdd5",
    role: "admin",
    label: "Admin",
    name: "Test Admin",
  },
];

export const INDORAMA_PROD_USERS: PresetUser[] = [
  {
    id: "3dbfe995-cb82-4328-8bf9-7c9311aa02b9",
    label: "Head of Department - PP",
    name: "Dinesh Prasad Bhakat",
  },
  {
    id: "642ef8bd-f5a1-4e85-8f15-9f80b570a351",
    label: "Head of Section - PP",
    name: "Monojit Sarkar",
  },
  {
    id: "44c7ec41-bd5c-4cf4-a5db-b9817ad8695d",
    label: "HSEF Ambassador - PP",
    name: "Eugene Ledisi Viele",
  },
  {
    id: "c915dc58-9d6b-4d81-a2db-f994ed213ad1",
    label: "Head of Department - PE",
    name: "Prasenjit Ghosh",
  },
  {
    id: "0ffaff6f-03c5-4480-a04c-7e8cb8f6a854",
    label: "Head of Section - PE",
    name: "Sanjeeb Bhowmick",
  },
  {
    id: "a5a8cf7f-1e14-453c-90c5-43b7fc783b73",
    label: "HSEF Ambassador - PE",
    name: "Nwafor Israel",
  },
  {
    id: "3f876436-0b2d-46f5-b439-cd3505b7bcb5",
    label: "Safety Manager - IFZ",
    name: "Tejas Pandit",
  },
  {
    id: "41573a95-5d27-48a1-a7cb-5e4f072aad44",
    label: "Safety Manager - IPL",
    name: "Dhinakaran K",
  },
  {
    id: "d342210f-e6f5-4a97-9fd0-53dde75d8475",
    label: "Admin",
    name: "Anjan Panja",
  },
  {
    id: "29c62317-173d-4b7d-b7da-a845c14cb1d7",
    label: "Raven-Admin",
    name: "Raven Admin",
  },
];

export const TEST_PROD_ACCOUNT_ID = "4691ad0d-c576-41db-aadb-04efe1aeb717";
export const INDORAMA_PROD_ACCOUNT_ID = "1138dbb7-f99a-4ce2-a250-fcc641b2076b";

// Matches the nmms SelectRole logic: non-prod → staging list; in prod,
// pick by account_id; anything else → no preset list.
export function getPresetUsers(
  accountId: string | undefined,
  isProd: boolean,
): PresetUser[] {
  if (!isProd) return STAGING_USERS;
  if (accountId === TEST_PROD_ACCOUNT_ID) return TEST_PROD_USERS;
  if (accountId === INDORAMA_PROD_ACCOUNT_ID) return INDORAMA_PROD_USERS;
  return [];
}

// Hostnames that are never prod: local dev, the *.staging.* deploys
// (e.g. ess-indorama.staging.startraven.com), and Vercel preview
// builds. Anything else in a production build is treated as prod —
// unknown hostnames fail safe toward the account-gated prod lists.
export function isProdHostname(hostname: string): boolean {
  return !(
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.includes(".staging.") ||
    hostname.endsWith(".vercel.app")
  );
}

export function isProdEnvironment(): boolean {
  // NODE_ENV alone cannot tell staging from prod: the staging deploy is
  // still a production *build* (CRA sets NODE_ENV to "production" for
  // any `npm run build`). And the account id can't discriminate either —
  // the staging account shares its UUID with INDORAMA_PROD_ACCOUNT_ID,
  // so before the hostname check staging fell into the prod branch of
  // getPresetUsers and showed INDORAMA_PROD_USERS.
  if (process.env.NODE_ENV !== "production") return false;
  return isProdHostname(window.location.hostname);
}
